import { ensureError } from '@passionware/platform-js';
import { escape } from 'lodash';
import { SupportService, SupportUser } from './SupportService';

const FEATUREBASE_APP_ID = '69429178724f14274410882e';
const SDK_URL = 'https://do.featurebase.app/js/sdk.js';
const SDK_ID = 'featurebase-sdk';
const READY_TIMEOUT_MS = 30_000;

declare global {
  interface Window {
    Featurebase: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

function ensureQueue() {
  if (typeof window.Featurebase !== 'function') {
    window.Featurebase = function (...args: unknown[]) {
      (window.Featurebase.q = window.Featurebase.q || []).push(args);
    };
  }
}

function loadSDK() {
  if (document.getElementById(SDK_ID)) return;
  const script = document.createElement('script');
  script.id = SDK_ID;
  script.src = SDK_URL;
  document.head.appendChild(script);
}

/**
 * The composer is rich text: `\n` collapses to a space, so each line becomes a
 * paragraph (an empty one needs a `<br>` to survive).
 */
function composerHtml(text: string): string {
  return text
    .split('\n')
    .map(line => `<p>${escape(line) || '<br>'}</p>`)
    .join('');
}

export function createFeaturebaseSupportService(): SupportService {
  let ready: Promise<void> | null = null;
  let pendingUser: SupportUser | undefined;

  function bootConfig() {
    return {
      appId: FEATUREBASE_APP_ID,
      hideDefaultLauncher: true,
      ...(pendingUser && {
        email: pendingUser.email,
        name: pendingUser.name,
        userId: pendingUser.userId
      })
    };
  }

  function boot(user?: SupportUser) {
    if (user) {
      pendingUser = user;
    }

    // Already starting or ready: refresh identity after the in-flight boot settles.
    // Do not start a second Featurebase boot while the first is pending (that cancels it).
    if (ready) {
      void ready.then(() => {
        ensureQueue();
        window.Featurebase('boot', bootConfig());
      });
      return;
    }

    loadSDK();
    ensureQueue();
    ready = new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        ready = null;
        reject(new Error('Support chat failed to load'));
      }, READY_TIMEOUT_MS);

      window.Featurebase('boot', bootConfig(), (error: unknown) => {
        window.clearTimeout(timeoutId);
        if (error) {
          ready = null;
          reject(ensureError(error));
          return;
        }
        resolve();
      });
    });
    void ready.catch(() => null);
  }

  async function booted() {
    if (!ready) {
      boot();
    }
    const pending = ready;
    if (!pending) {
      throw new Error('Support chat failed to load');
    }
    await pending;
    ensureQueue();
  }

  return {
    boot,

    async show() {
      await booted();
      window.Featurebase('show', 'messages');
    },

    async showNewMessage(initialText) {
      await booted();
      window.Featurebase('showNewMessage', composerHtml(initialText));
    },

    shutdown() {
      ready = null;
      pendingUser = undefined;
      ensureQueue();
      window.Featurebase('destroy');
    }
  };
}
